import { 
  Typography, 
  Box, 
  Button, 
  makeStyles,
  Paper,
  Grid,
  Snackbar,
  TextField,
  CircularProgress,
  FormHelperText
} from "@material-ui/core";
import InfoIcon from '@material-ui/icons/Info';
import { IdentityApplication } from "libs/resources/identity/applications/PublisherApplicationService";
import React, { useEffect, useState } from "react";
import { Alert } from "@material-ui/lab";
import { ImageFileUpload } from "./ImageFileUpload";
import ConsentService from "pages/home/applications/services/ConsentService";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import ConsentApprovalService from "pages/home/applications/services/ConsentApprovalService";
import { useSelector } from "react-redux";
import { ApprovalNotifier } from "./ApprovalNotifier";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    minHeight: '476px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius
  },
  form: {
    '& > *': {
      marginBottom: theme.spacing(3)
    }
  },
  fileUpload: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  domainInput: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  submitButton: {
    float: 'right',
    marginTop: theme.spacing(2)
  },
  formField: {
    marginBottom: theme.spacing(3)
  },
  errorText: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(1.5),
    marginTop: theme.spacing(0.5)
  },
  paper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(4),
    backgroundColor: '#ffffff',
    borderRadius: 4,
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
  },
  formTitle: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: theme.spacing(3),
  },
  formDescription: {
    marginBottom: theme.spacing(4),
    color: '#666',
  },
  fieldContainer: {
    marginBottom: theme.spacing(3),
  },
  fieldLabel: {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    color: '#464b52',
    fontWeight: 400,
    lineHeight: '21px',
    letterspacing: '0.15%',
    '& .required': {
      color: theme.palette.error.main,
      marginLeft: theme.spacing(0.5),
    },
  },
  infoAlert: {
    borderLeft: `12px solid ${theme.palette.primary.main}`,
    backgroundColor: 'rgba(33, 150, 243, 0.05)',
    marginBottom: theme.spacing(3)
  },
  statusImage: {
    width: '150px', 
    height: 'auto', 
    marginTop: '16px', 
    marginRight: "60px"
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-error': {
        '& fieldset': {
          borderColor: theme.palette.error.main,
        },
      },
    },
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  }
}));

// Define the ConsentFormProps interface
interface ConsentFormProps {
  application: IdentityApplication;
  reloadApplication: () => void;
}

interface FormValues {
  termsOfService: string;
  privacyPolicy: string;
  logo: string;
}

interface FormErrors {
  termsOfService?: string;
  privacyPolicy?: string;
  logo?: string;
}

export const ConsentForm = React.memo(({ application, reloadApplication }: ConsentFormProps) => {
  const classes = useStyles();
  const [formValues, setFormValues] = useState<FormValues>({
    termsOfService: '',
    privacyPolicy: '',
    logo: ''
  });
  const [initialValues, setInitialValues] = useState<FormValues>({
    termsOfService: '',
    privacyPolicy: '',
    logo: ''
  });
  const [formDisabled, setFormDisabled] = useState(false);
  const [landingPage, setLandingPage] = useState('');
  const [authorizedDomain, setAuthorizedDomain] = useState('');
  const [authorizedDomains, setAuthorizedDomains] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [applicationLogo, setApplicationLogo] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );

  const consumerKey = application.applicationGatewayResponses?.filter(
    response => response.organisationName === identityEnvironment
  )?.[0]?.consumerKey;

  const { subscribe: getConsentDetails, state: consentState } = useAsyncAction(
    () => ConsentService.getConsent(consumerKey as string),
    {
      map: (data: any) => {
        if (data) {
          const newValues = {
            termsOfService: data.termsOfServiceUrl || '',
            privacyPolicy: data.policyUrl || '',
            logo: data.applicationLogo || ''
          };
          setFormValues(newValues);
          setInitialValues(newValues);
          setApplicationLogo(data.applicationLogo || null);
          
          // If consent data already exists with all required fields, disable the form
          if (data.termsOfServiceUrl && data.policyUrl && data.applicationLogo) {
            setFormDisabled(true);
          }
        }
        return data;
      },
      error: (error: any) => {
        console.error('Error fetching consent details:', error);
      }
    }
  );

  const { subscribe: submitConsentDetails, state: submitState } = useAsyncAction(
    () => {
      const submitData = new FormData();
      submitData.append('termsOfServiceUrl', formValues.termsOfService || '');
      submitData.append('privacyPolicyUrl', formValues.privacyPolicy || '');
      
      if (logoFile) {
        submitData.append('applicationLogo', logoFile);
      }
      
      return ConsentService.updateConsent(
        application.applicationGatewayResponses?.filter(
          response => response.organisationName === identityEnvironment
        )?.[0]?.consumerKey ?? application.applicationId, 
        submitData
      );
    },
    {
      map: (data: any) => {
        setSubmitted(true);
        return data;
      },
      error: (error: any) => {
        console.error('Error submitting consent form:', error);
        
        // Don't show error notification for 403 Forbidden errors
        if (error?.response?.status !== 403) {
          const errorMessage = resolveAPIErrorMessage(error,
                              'An error occurred while submitting the form. Please try again.');
          setSubmitError(errorMessage);
        }
      }
    }
  );

  useEffect(() => {
    // Only trigger if we have a consumerKey and haven't started loading yet
    if (consumerKey && consentState.status === "INIT") {
      getConsentDetails();
    }
  }, [consumerKey, getConsentDetails]);

  const handleInputChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormValues(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Live URL validation
    if ((field === 'termsOfService' || field === 'privacyPolicy') && value) {
      // Only perform validation if the user has entered something substantial
      if (value.length > 5) {
        const validationError = getUrlValidationError(value);
        if (validationError) {
          setErrors(prev => ({
            ...prev,
            [field]: validationError
          }));
        }
      }
    }
  };

  const handleLogoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues(prev => ({
      ...prev,
      logo: e.target.value
    }));
    
    // Clear error on change
    if (errors.logo) {
      setErrors(prev => ({
        ...prev,
        logo: undefined
      }));
    }
  };

  const handleLogoFileChange = (file: File) => {
    setLogoFile(file);
    
    // Clear error on change
    if (errors.logo) {
      setErrors(prev => ({
        ...prev,
        logo: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Validate Terms of Service URL
    const termsOfServiceError = getUrlValidationError(formValues.termsOfService);
    if (termsOfServiceError) {
      newErrors.termsOfService = termsOfServiceError;
    }
    
    // Validate Privacy Policy URL
    const privacyPolicyError = getUrlValidationError(formValues.privacyPolicy);
    if (privacyPolicyError) {
      newErrors.privacyPolicy = privacyPolicyError;
    }
    
    // Validate Logo
    if (!logoFile && !formValues.logo) {
      newErrors.logo = 'Application Logo is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getUrlValidationError = (url: string): string | null => {
    if (!url) {
      return 'URL is required';
    }

    // Check if URL starts with http:// or https://
    if (!url.match(/^https?:\/\//)) {
      return 'URL must start with http:// or https://';
    }

    // Check for consecutive multiple slashes after http:// or https://
    if (url.match(/^https?:\/\/.*?\/\//)) {
      return 'URL cannot contain consecutive slashes after the protocol (http:// or https://)';
    }

    try {
      const urlObj = new URL(url);
      
      // Check if hostname is valid (domain.tld)
      const hostnameRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      if (!hostnameRegex.test(urlObj.hostname)) {
        return 'URL must contain a valid domain name (e.g., example.com)';
      }
      
      // Valid URL with proper domain
      return null;
    } catch (e) {
      return 'Please enter a valid URL';
    }
  };

  const isValidUrl = (url: string): boolean => {
    return getUrlValidationError(url) === null;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    
    // Reset any previous errors
    setSubmitError(null);
    
    // Submit the form using the async action
    submitConsentDetails();
  };

  const refresh = () => {
    reloadApplication();
  }
  
  const clearError = () => {
    setSubmitError(null);
  }

  const isFormValid = (): boolean => {
    // Basic validation - fields must be present and valid URLs
    const hasValidData = 
      !submitted && 
      !submitState.loading && 
      !!formValues.termsOfService && 
      !!formValues.privacyPolicy && 
      (!!logoFile || !!formValues.logo) &&
      isValidUrl(formValues.termsOfService) &&
      isValidUrl(formValues.privacyPolicy);
    
    // Check if anything has changed compared to initial values
    const termsChanged = formValues.termsOfService !== initialValues.termsOfService;
    const privacyChanged = formValues.privacyPolicy !== initialValues.privacyPolicy;
    const logoChanged = !!logoFile || (formValues.logo !== initialValues.logo);
    
    
    // Only enable submit if values are valid AND something has changed
    return hasValidData && (termsChanged || privacyChanged || logoChanged);
  };

  if (consentState.loading) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.loader}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      {!consentState.value?.applicationLogo && !formDisabled && (<Alert 
        severity="info" 
        variant="outlined" 
        className={classes.infoAlert}
        iconMapping={{ 
          info: <InfoIcon color="primary" />,
        }} 
      >
        <Typography variant="subtitle1" style={{ marginLeft: 8 }}>
          Complete the below details to get Third party user consent from the end user
        </Typography>
      </Alert>)}
      
      {formDisabled && (
        <Alert 
          severity="info" 
          variant="outlined" 
          className={classes.infoAlert}
          iconMapping={{ 
            info: <InfoIcon color="primary" />,
          }} 
        >
          <Typography variant="subtitle1" style={{ marginLeft: 8 }}>
            Consent details have already been provided and cannot be modified.
          </Typography>
        </Alert>
      )}
      
      <Snackbar
        open={submitState.loaded && !submitState.error}
        autoHideDuration={6000}
        onClose={refresh}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <>
          <Alert 
            severity="success" 
            variant="filled" 
            onClose={refresh} 
            iconMapping={{
              success: <CheckCircleIcon />,
            }} 
            style={{ marginBottom: "30px" }}
          >
            Third party consent changes were saved.
          </Alert>
        </>
      </Snackbar>
      
      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <>
          <Alert 
            severity="error" 
            variant="filled" 
            onClose={clearError} 
            iconMapping={{
              error: <ErrorIcon />,
            }} 
            style={{ marginBottom: "30px" }}
          >
            {submitError}
          </Alert>
        </>
      </Snackbar>
      
      <Box className={classes.form}>
        <Grid container spacing={2}>
          {/*
          TODO: Enable this section when TID implements landing page
          <Grid item xs={12} md={6}>
            <Box className={classes.fieldContainer}>
              <Typography className={classes.fieldLabel}>
                Application landing page <span className="required">*</span>
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                required
                disabled={submitted}
                value={landingPage}
                onChange={(e) => setLandingPage(e.target.value)}
                placeholder="Your application landing page URL to explore the services"
                className={classes.inputField}
              />
            </Box>
          </Grid>

            </Box>
          </Grid>*/}

          <Grid item xs={12} md={6}>
            <Box className={classes.fieldContainer}>
              <Typography className={classes.fieldLabel}>
                Application Logo <span className="required">*</span>
              </Typography>
              <ImageFileUpload
                value={formValues.logo}
                onChange={handleLogoInputChange}
                required={true}
                disabled={submitted || consentState.status === "LOADING" || formDisabled}
                onFileChange={handleLogoFileChange}
                accept="image/svg+xml"
                placeholder="Your Application logo to be displayed in consent screen"
                error={!!errors.logo}
                helperText={errors.logo}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}></Grid>
          <Grid item xs={12} md={6}>
            <Box className={classes.fieldContainer}>
              <Typography className={classes.fieldLabel}>
                Terms of Service <span className="required">*</span>
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                required
                disabled={submitted || formDisabled}
                value={formValues.termsOfService}
                onChange={handleInputChange('termsOfService')}
                placeholder="Link to the web page that have your company terms and services"
                error={!!errors.termsOfService}
                className={classes.inputField}
                InputProps={{
                  endAdornment: errors.termsOfService ? (
                    <ErrorIcon style={{ color: '#f44336' }} />
                  ) : null,
                }}
              />
              {errors.termsOfService && (
                <FormHelperText className={classes.errorText}>
                  {errors.termsOfService}
                </FormHelperText>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.fieldContainer}>
              <Typography className={classes.fieldLabel}>
                Privacy Policy <span className="required">*</span>
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                required
                disabled={submitted || formDisabled}
                value={formValues.privacyPolicy}
                onChange={handleInputChange('privacyPolicy')}
                placeholder="Link to the web page that have your company Privacy policies"
                error={!!errors.privacyPolicy}
                className={classes.inputField}
                InputProps={{
                  endAdornment: errors.privacyPolicy ? (
                    <ErrorIcon style={{ color: '#f44336' }} />
                  ) : null,
                }}
              />
              {errors.privacyPolicy && (
                <FormHelperText className={classes.errorText}>
                  {errors.privacyPolicy}
                </FormHelperText>
              )}
            </Box>
          </Grid>
             
          {/*
          TODO: Enable this section when Approval Changes implemented
          <Grid item xs={12} style={{display: "flex", justifyContent: "flex-end"}}>
            {["pending_approval", "approved", "rejected", "denied"].includes(`${application.consent}`) && (
              <img 
                src={`/assets/consent-${application.consent}.png`} 
                alt={`Consent ${application.consent}`} 
                className={classes.statusImage}
              />
            )}
          </Grid>*/}
          
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Button 
                variant="contained" 
                color="primary" 
                disabled={!isFormValid() || submitState.loading}
                className={classes.submitButton}
                onClick={handleSubmit}
                style={{ 
                  borderRadius: '4px',
                  textTransform: 'none',
                  padding: '8px 24px',
                  fontWeight: 500
                }}
              >
                {submitState.loading ? 
                  <CircularProgress size={24} color="inherit" /> : 
                  "Submit"
                }
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
});
